import React, {useEffect, useState} from 'react';
import Header from '../../components/Header';
import {Footer} from '../../components/Footer';
import {BetSchema, BetsService, MatchesService, MatchWithBetSchema, UserScoreSchema} from "../../api";
import {displayMatchStatus, formatDateTime} from "../../utils/helpers";
import {BetResultTooltip} from "../../components/BetResultTooltipProps/BetResultTooltipProps";
import {Skeleton} from "../../components/Skeleton";
import {Rating} from "../../components/Rating";

const Bets: React.FC = () => {
    const [matches, setMatches] = useState<MatchWithBetSchema[]>([]);
    const [place, setPlace] = useState<UserScoreSchema>();
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        Promise.all([
            BetsService.getUserScore(),
            MatchesService.listMatchesWithBet(),
        ]).then(([userScoreData, matchesData]) => {
            setPlace(userScoreData);
            setMatches(matchesData);
        }).finally(() => setLoading(false));
    }, []);

    const formatBetScore = (bet: BetSchema) => {
        const parts: string[] = [`${bet.home}:${bet.away}`];
        if (bet.overtime_home !== null && bet.overtime_away !== null) {
            parts.push(`(${bet.overtime_home}:${bet.overtime_away})`);
        }
        if (bet.penalty_home !== null && bet.penalty_away !== null) {
            parts.push(`пен. ${bet.penalty_home}:${bet.penalty_away}`);
        }
        return parts.join(' ');
    };

    const getTotalResult = (bet: BetSchema) => {
        if (bet.classic_points === null || bet.classic_points === undefined) return null;
        return (bet.classic_points ?? 0) + (bet.stake_profit ?? 0);
    };

    if (loading) return <Skeleton />;

    return (
        <>
            <Header />
            {
                <div className='rates'>
                    {place && <Rating place={place} />}
                    <h3 className='rates__title'>Мои прогнозы</h3>

                    {matches &&
                        <div className='rates__scroll'>
                        <table className='rates__table'>
                            <thead>
                            <tr>
                                <th>Матч</th>
                                <th className='rates__cell--center rates__col--status'>Статус</th>
                                <th className='rates__cell--center'>Время</th>
                                <th className='rates__cell--center'>Счёт</th>
                                <th className='rates__cell--center'>Прогноз</th>
                                <th className='rates__cell--center'>Ставка</th>
                                <th className='rates__cell--right'>Итог</th>
                            </tr>
                            </thead>
                            <tbody>
                            {matches.map((match) => {
                                const bet = match.bet;
                                const totalResult = bet ? getTotalResult(bet) : null;
                                const isClosed = match.status === 'CLOSED';
                                const isLive = match.status === 'LIVE';

                                return (
                                    <tr key={match.id}>
                                        <td className='rates__cell--match-bets'>
                                            <a
                                                href={`/matches/${match.id}`}
                                                className='rates__match-link'
                                            >
                                                <span>{match.home?.team.name}</span>
                                                <span className='rates__match-sep'> – </span>
                                                <span>{match.away?.team.name}</span>
                                            </a>
                                            <div className='rates__cell--small'>{match.round_name}{match.round?.group && `, ${match.round.group}`}</div>
                                        </td>
                                        <td className='rates__cell--center rates__col--status'>
                                                <span
                                                    className={`rates__status rates__status--${match.status.toLowerCase()}`}>
                                                    {displayMatchStatus(match.status)}
                                                </span>
                                        </td>
                                        <td className='rates__cell--center rates__cell--secondary'>
                                            {formatDateTime(match.scheduled_at)}
                                        </td>
                                        <td className='rates__cell--center rates__cell--score'>
                                            {isClosed || isLive ? (
                                                match.home?.overtime_score !== null ? (
                                                    <>
                                                        <div>{match.home?.overtime_score}:{match.away?.overtime_score}</div>
                                                        <div className='rates__cell--small'>осн. {match.home?.score}:{match.away?.score}</div>
                                                        {match.home?.penalty_score !== null && (
                                                            <div className='rates__cell--small'>пен. {match.home?.penalty_score}:{match.away?.penalty_score}</div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>{match.home?.score}:{match.away?.score}</>
                                                )
                                            ) : (
                                                <span className='rates__cell--empty'>—</span>
                                            )}
                                        </td>
                                        <td className='rates__cell--center rates__cell--score'>
                                            {bet ? (
                                                formatBetScore(bet)
                                            ) : (
                                                <span className='rates__cell--empty'>—</span>
                                            )}
                                        </td>
                                        <td className='rates__cell--center'>
                                            {bet && bet.stake && bet.stake > 0 ? (
                                                <>
                                                    {bet.stake}
                                                    {bet.is_all_in && (
                                                        <span className='rates__cell__badge rates__cell__badge--allin'>ALL IN</span>
                                                    )}
                                                    {bet.is_golden && (
                                                        <span
                                                            className='rates__cell__badge rates__cell__badge--golden'>×2</span>
                                                    )}
                                                </>
                                            ) : (
                                                <span className='rates__cell--empty'>—</span>
                                            )}
                                        </td>
                                        <td className={`rates__cell--right ${
                                            totalResult !== null
                                                ? totalResult >= 0
                                                    ? 'rates__cell--positive'
                                                    : 'rates__cell--negative'
                                                : 'rates__cell--empty'
                                        }`}>
                                            {totalResult !== null && bet ? (
                                                <BetResultTooltip bet={bet} totalResult={totalResult}>
                                                    {`${totalResult >= 0 ? '+' : ''}${totalResult}`}
                                                </BetResultTooltip>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                    }
                </div>
            }
            <Footer />
        </>
    );
};

export default React.memo(Bets);